import type { Collection, ScheduledAction, ScheduledActionProps } from 'contentful-management'
import { middlewareGateway } from '~/gateway/middleware.gateway'
import type {
  ScheduledActionItem,
  SchedulingInfo,
  ScheduleAction,
  ScheduleEntry,
  PanelStatus,
  EntryDisplayDetails,
} from '~/types/ContentfulPreview'

export const useScheduledActions = () => {
  const createScheduleEntry = (item: ScheduledActionItem): ScheduleEntry => ({
    action: item.action,
    scheduledFor: {
      datetime: new Date(item.scheduledFor.datetime),
    },
  })

  const createPanelSchedule = (panelStatus: PanelStatus | undefined) => ({
    label: panelStatus?.label,
    status: {
      isDraft: panelStatus?.status.isDraft || false,
      isChanged: panelStatus?.status.isChanged || false,
      isPublished: panelStatus?.status.isPublished || false,
      isArchived: panelStatus?.status.isArchived || false,
    },
    schedule: [] as ScheduleEntry[],
  })

  const dateifySchedule = (s: ScheduleAction): ScheduleAction => ({
    ...s,
    scheduledFor: {
      ...s.scheduledFor,
      datetime: new Date(s.scheduledFor.datetime),
    },
  })

  const containsActionImmediatelyBeforeDate = (
    schedule: ScheduleAction[],
    pointInTime: Date,
    action: ScheduleAction['action'],
  ): boolean => {
    if (schedule.length === 0) return false

    const sortedSchedule = [...schedule].sort(
      (a, b) => a.scheduledFor.datetime.getTime() - b.scheduledFor.datetime.getTime(),
    )

    const actionBefore = sortedSchedule.reduce<ScheduleAction | undefined>((prev, curr) => {
      return curr.scheduledFor.datetime.getTime() <= pointInTime.getTime() ? curr : prev
    }, undefined)

    return actionBefore?.action === action
  }

  const getActionAtOrBeforeDate = (
    schedule: ScheduleAction[],
    pointInTime: Date,
  ): ScheduleAction['action'] | undefined => {
    if (schedule.length === 0) return undefined

    const sortedSchedule = [...schedule].sort(
      (a, b) => a.scheduledFor.datetime.getTime() - b.scheduledFor.datetime.getTime(),
    )

    const actionBeforeOrAt = sortedSchedule.reduce<ScheduleAction | undefined>((prev, curr) => {
      return curr.scheduledFor.datetime.getTime() <= pointInTime.getTime() ? curr : prev
    }, undefined)

    return actionBeforeOrAt?.action
  }

  const getLatestScheduledDate = (schedule: ScheduleAction[]): Date | undefined => {
    if (!schedule.length) return undefined

    const sortedSchedule = [...schedule].sort(
      (a, b) => a.scheduledFor.datetime.getTime() - b.scheduledFor.datetime.getTime(),
    )

    return sortedSchedule[sortedSchedule.length - 1]?.scheduledFor.datetime
  }

  const shouldShowEntryAtPointInTime = (schedulingInfo: SchedulingInfo, pointInTime: Date): EntryDisplayDetails => {
    const { isDraft, isPublished, isChanged } = schedulingInfo.status
    const actionAtOrBefore = getActionAtOrBeforeDate(schedulingInfo.schedule, pointInTime)

    if (isDraft) {
      const latestScheduledDate = getLatestScheduledDate(schedulingInfo.schedule)

      // Hide draft before last scheduled action time
      if (!latestScheduledDate || pointInTime.getTime() < latestScheduledDate.getTime()) {
        return { show: false }
      }

      // At/after schedule time, decide by action at/before that time
      return { show: actionAtOrBefore === 'publish' ? 'preview' : false }
    }

    if (isPublished) {
      const latestScheduledDate = getLatestScheduledDate(schedulingInfo.schedule)

      // No latest schedule date => keep published visible
      if (!latestScheduledDate) {
        return { show: 'published' }
      }

      // Determine what action is scheduled at the latest scheduled date
      const latestScheduledAction = getActionAtOrBeforeDate(schedulingInfo.schedule, latestScheduledDate)

      // If latest action is unpublish:
      // - before latestScheduledDate => still show published
      // - on/after latestScheduledDate => hide
      if (latestScheduledAction === 'unpublish') {
        return {
          show: pointInTime.getTime() < latestScheduledDate.getTime() ? 'published' : false,
        }
      }

      return { show: 'published' }
    }

    if (isChanged) {
      return { show: actionAtOrBefore === 'publish' ? 'preview' : false }
    }

    return { show: false }
  }

  const getEntryDisplayInfo = (
    ids: string[],
    previewDateISOString: string,
    schedulingInfoForIds: SchedulingInfo<ScheduleAction>,
    panelStatuses?: PanelStatus[],
  ): Record<string, EntryDisplayDetails> => {
    const previewByDate = new Date(previewDateISOString)
    const displayInfo: Record<string, EntryDisplayDetails> = {}

    ids.forEach((id) => {
      const schedulingInfo = schedulingInfoForIds[id]

      if (!schedulingInfo?.status) {
        const panelStatus = panelStatuses?.find((p: any) => p.id === id)
        if (panelStatus?.status.isDraft) {
          displayInfo[id] = { show: !schedulingInfo ? false : 'preview', label: panelStatus.label }
        } else if (panelStatus?.status.isPublished) {
          displayInfo[id] = { show: 'published', label: panelStatus.label }
        } else {
          displayInfo[id] = { show: false, label: panelStatus?.label }
        }
        return
      }

      const dateifiedInfo: SchedulingInfo<ScheduleAction> = {
        ...schedulingInfo,
        schedule: schedulingInfo.schedule.map(dateifySchedule),
      }
      displayInfo[id] = shouldShowEntryAtPointInTime(dateifiedInfo, previewByDate)
    })

    return displayInfo
  }

  const getPanelStatus = (panel: LocalePageEntry): PanelInfo['status'] => {
    const { revision, updatedAt, publishedAt, firstPublishedAt } = panel.sys
    const isRevisionZero = revision === 0
    const isUnpublished = !publishedAt
    const hasFirstPublished = !!firstPublishedAt
    const isDraft = isRevisionZero || (revision && updatedAt && isUnpublished)

    return {
      isDraft: isDraft,
      isChanged: (revision || 0) > 0 && !isDraft && (updatedAt || '') > (publishedAt || ''),
      isPublished: (revision || 0) > 0 && !!(publishedAt && publishedAt >= (updatedAt || '')),
      isUnpublished: !!revision && isUnpublished && hasFirstPublished,
      isArchived: !updatedAt && isUnpublished && !hasFirstPublished && !revision,
    }
  }

  const getPanelInfo = (panel: LocalePageEntry, opts?: { isParentPage?: boolean }): PanelInfo => ({
    id: panel.sys.id,
    label: (panel.fields as any)?.label,
    contentType: panel.sys.contentType?.sys.id,
    status: getPanelStatus(panel),
    dateInfo: {
      createdAt: panel.sys.createdAt,
      firstPublishedAt: panel.sys.firstPublishedAt,
      publishedAt: panel.sys.publishedAt,
      updatedAt: panel.sys.updatedAt,
      archivedAt: panel.sys.archivedAt,
      publishedVersion: panel.sys.publishedVersion,
      revision: panel.sys.revision,
    },
    isParentPage: opts?.isParentPage ?? false,
  })

  // Define an interface for the query parameters
  interface ScheduledActionsQueryParams {
    previewIds: string[]
    previewEnv: string
    'sys.status'?: string
  }

  async function fetchScheduledActions(queryParams: ScheduledActionsQueryParams) {
    const safeSearchParams = toContentfulSearchParams({
      ...queryParams,
      'sys.status': 'scheduled',
    })
    try {
      const response = await middlewareGateway(`/next-gen/cntfl/scheduledActions?${safeSearchParams.toString()}`, {
        headers: { 'Content-Type': 'application/json' },
        method: 'GET',
      })
      return response as Collection<ScheduledAction, ScheduledActionProps>
    } catch (error) {
      throw new Error(error)
    }
  }

  async function getAllScheduledActions(ids: string[], previewEnv: string, panelStatuses?: PanelStatus[]) {
    const queryParams: ScheduledActionsQueryParams = {
      previewIds: ids,
      previewEnv: previewEnv,
    }

    const response = await fetchScheduledActions(queryParams)
    const schedules: Record<string, any> = {}
    const items = response?.items || []

    items.forEach((item: any) => {
      const id = item.entity.sys.id
      const panelStatus = panelStatuses?.find((p: any) => p.id === id)
      const entry = createScheduleEntry(item)

      if (schedules[id]) {
        schedules[id].schedule.push(entry)
      } else {
        const schedule = createPanelSchedule(panelStatus)
        schedule.schedule.push(entry)
        schedules[id] = schedule
      }
    })

    return schedules
  }

  return {
    getPanelStatus,
    getPanelInfo,
    getEntryDisplayInfo,
    getAllScheduledActions,
    fetchScheduledActions,
  }
}
